import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListWarehouseShipmentsResponse,
  CreateReceiptRequest,
  CreateReceiptResponse,
  ListReceivablePosResponse,
  ListReceiptHistoryResponse,
  type ReceiptHistoryLine,
  type ReceiptHistoryPoRef,
  ListWarehouseShipmentBoxesResponse,
  ListWarehouseWorkChargesResponse,
  ListWarehouseInvoicesResponse,
  WarehouseInvoiceDetailResponse,
  RecordWarehouseInvoicePaymentResponse,
  ReverseWarehouseInvoicePaymentResponse,
  WarehouseServiceCatalogItem,
  WarehouseWorkChargeResponse,
  type CreateWarehouseShipmentBoxRequest,
  type ReplaceWarehouseShipmentBoxContentsRequest,
  type CreateWarehouseWorkChargeRequest,
  type ReverseWarehouseWorkChargeRequest,
  type CreateWarehouseInvoiceRequest,
  type UpdateWarehouseInvoiceDraftRequest,
  type TransitionWarehouseInvoiceRequest,
  type CreateWarehouseCreditNoteRequest,
  type RecordWarehouseInvoicePaymentRequest,
  type ReverseWarehouseInvoicePaymentRequest,
  type WarehouseInvoiceStatus,
  type WarehouseAccountingTreatment,
  type WarehouseBillingBasis,
  WarehouseShipmentBox,
  WarehouseFacility,
  WarehouseCarton,
  ListWarehouseCartonsResponse,
  WarehouseCartonRecommendationResponse,
  type WarehouseCartonRecommendationRequest,
  Uuid,
  ListWarehousePrepBatchesResponse,
  WarehousePrepBatchResponse,
  ListWarehousePrepBoxesResponse,
  WarehousePrepBox,
  type ClaimWarehousePrepBatchRequest,
  type MapWarehousePrepLineRequest,
  type ReplaceWarehousePrepBoxContentsRequest,
  type FinalizeWarehousePrepBoxRequest,
  type ReopenWarehousePrepBoxRequest,
  CreateWarehousePrepWorkChargeRequest,
  type PrepStatus,
  PrepAmazonHandoffResponse,
  WarehousePrepDispatchResponse,
  DispatchWarehousePrepBatchRequest,
  type ConfirmPrepAmazonHandoffRequest,
  type RetryPrepAmazonLabelsRequest,
  ListWarehousePeriodExpenseAttestationsResponse,
  AttestWarehousePeriodExpensesResponse,
  type AttestWarehousePeriodExpensesRequest,
  WarehouseExpensePeriodPreviewResponse,
  ConfirmReceiptResponse,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import { useRef } from 'react';
import { z } from 'zod';
// shortDateLabel/shipmentLinePreviewLabel/shipmentPreviewMoreLabel are
// fully generic (numbers + nullable strings in, string out) — reused as-is
// for the receipt-history row rather than duplicated. See shipments.ts.
export { shortDateLabel, shipmentLinePreviewLabel, shipmentPreviewMoreLabel } from './shipments';

export const WAREHOUSE_BILLING_QUERY_KEYS = {
  facilities: () => ['warehouse-billing', 'facilities'] as const,
  services: (warehouseId?: string | null) =>
    ['warehouse-billing', 'services', warehouseId ?? 'all'] as const,
  shipments: (warehouseId?: string | null) =>
    ['warehouse-billing', 'shipments', warehouseId ?? 'all'] as const,
  boxes: (warehouseId: string | null | undefined, shipmentId: string) =>
    ['warehouse-billing', 'boxes', warehouseId ?? 'all', shipmentId] as const,
  charges: (warehouseId?: string | null, shipmentId?: string | null, filters?: string) =>
    filters === undefined
      ? (['warehouse-billing', 'charges', warehouseId ?? 'all', shipmentId ?? 'all'] as const)
      : ([
          'warehouse-billing',
          'charges',
          warehouseId ?? 'all',
          shipmentId ?? 'all',
          filters,
        ] as const),
  invoices: (warehouseId?: string | null, filters?: string) =>
    filters === undefined
      ? (['warehouse-billing', 'invoices', warehouseId ?? 'all'] as const)
      : (['warehouse-billing', 'invoices', warehouseId ?? 'all', filters] as const),
  invoice: (warehouseId: string | null | undefined, invoiceId: string) =>
    ['warehouse-billing', 'invoice', warehouseId ?? 'all', invoiceId] as const,
  periodExpenseAttestations: () => ['warehouse-billing', 'period-expense-attestations'] as const,
} as const;

/** Shared prefix for every Ready-to-Prep queue query (including future filters). */
export const WAREHOUSE_PREP_QUERY_KEYS = {
  all: ['warehouse-prep'] as const,
  queue: ['warehouse-prep', 'queue'] as const,
  queueFor: (warehouseId?: string | null) =>
    ['warehouse-prep', 'queue', warehouseId ?? 'all'] as const,
  batch: (batchId: string) => ['warehouse-prep', 'batch', batchId] as const,
  boxes: (batchId: string) => ['warehouse-prep', 'boxes', batchId] as const,
  handoff: (batchId: string) => ['warehouse-prep', 'handoff', batchId] as const,
} as const;

export function prepStatusCopy(status: PrepStatus, remainingVendorUnits?: number): string {
  switch (status) {
    case 'ready_to_prep':
      return 'Received and waiting to be claimed.';
    case 'in_prep':
      return `${remainingVendorUnits ?? 0} vendor units still need a box`;
    case 'awaiting_amazon':
      return 'Awaiting Amazon instructions';
    case 'label_ready':
      return 'Labels are ready';
    case 'dispatched':
      return 'Dispatched';
  }
}

const INVOICE_STATUS_LABELS: Record<WarehouseInvoiceStatus, string> = {
  draft: 'Draft — not sent',
  submitted: 'Awaiting seller review',
  approved: 'Approved — awaiting payment',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  disputed: 'Needs changes',
  void: 'Voided',
};

export function invoiceStatusLabel(status: WarehouseInvoiceStatus): string {
  return INVOICE_STATUS_LABELS[status];
}

export function accountingTreatmentLabel(treatment: WarehouseAccountingTreatment): string {
  return treatment === 'capitalizable'
    ? 'Added to item cost when sold'
    : 'Warehouse operating expense';
}

const BASIS_LABELS: Record<WarehouseBillingBasis, string> = {
  vendor_unit: 'Vendor units',
  listing_unit: 'Listing units',
  box: 'Boxes',
  weight: 'Weight',
  volume: 'Volume',
  time: 'Time',
  fixed: 'Fixed charge',
};

export function humanizeWarehouseBasis(basis: WarehouseBillingBasis): string {
  return BASIS_LABELS[basis];
}

const BLOCKER_LABELS: Record<string, string> = {
  MISSING_MULTIPACK_SNAPSHOT: 'The item mapping is missing its pack-size snapshot.',
  UNALLOCATED_SHIPMENT_UNITS: 'Some shipment units are not available to allocate yet.',
  ZERO_ELIGIBLE_WEIGHT: 'There are no eligible units for this allocation.',
  MISSING_MEASURE: 'The required allocation measure is missing.',
  INVALID_TARGET_SET: 'The selected allocation targets are not valid.',
  TARGETS_REQUIRED: 'Select allocation targets before saving this charge.',
  LOT_FACILITY_MISMATCH: 'The inventory belongs to a different facility.',
};

export function humanizeWarehouseBlocker(blocker: string | null | undefined): string {
  if (!blocker) return 'Allocation needs review.';
  return BLOCKER_LABELS[blocker] ?? blocker.replaceAll('_', ' ').toLowerCase();
}

export function humanizeWarehouseSource(
  source: 'receipt' | 'shipment' | 'shipment_line' | 'box' | null | undefined
): string {
  switch (source) {
    case 'receipt':
      return 'Receiving record';
    case 'shipment':
      return 'Shipment';
    case 'shipment_line':
      return 'Shipment item';
    case 'box':
      return 'Packing box';
    default:
      return 'Source not identified';
  }
}

export function resolveWarehouseFacility(
  facilities: Array<{ id: string; name: string }>,
  preferredId?: string | null
): { id: string; name: string } | null {
  if (preferredId) return facilities.find((facility) => facility.id === preferredId) ?? null;
  return facilities.length === 1 ? (facilities[0] ?? null) : null;
}

export function formatWarehouseCents(cents: number, currency = 'USD'): string {
  const normalizedCurrency = currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(normalizedCurrency)) {
    throw new RangeError(`Invalid currency code: ${currency}`);
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedCurrency,
    }).format(cents / 100);
  } catch {
    throw new RangeError(`Invalid currency code: ${currency}`);
  }
}

export function formatWarehouseQuantityMillis(quantityMillis: number): string {
  return (quantityMillis / 1000).toFixed(3).replace(/\.?0+$/, '');
}

export type WarehouseQuantityMillisResult =
  | { ok: true; quantityMillis: number }
  | { ok: false; error: string };

/** Parse the warehouse quantity input without floating-point rounding. */
export function parseWarehouseQuantityMillis(raw: string): WarehouseQuantityMillisResult {
  if (typeof raw !== 'string' || raw.length === 0 || raw.trim().length === 0) {
    return { ok: false, error: 'Quantity is required.' };
  }
  if (raw !== raw.trim()) {
    return { ok: false, error: 'Enter a positive decimal quantity.' };
  }
  const match = /^(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match) return { ok: false, error: 'Enter a positive decimal quantity.' };
  const fraction = match[2] ?? '';
  if (fraction.length > 3) {
    return { ok: false, error: 'Quantity may have at most 3 decimal places.' };
  }
  const quantityMillis = BigInt(match[1] ?? '0') * 1000n + BigInt(fraction.padEnd(3, '0') || '0');
  if (quantityMillis <= 0n) {
    return { ok: false, error: 'Quantity must be greater than zero.' };
  }
  if (quantityMillis > 2147483647n) {
    return { ok: false, error: 'Quantity is larger than the supported maximum.' };
  }
  return { ok: true, quantityMillis: Number(quantityMillis) };
}

export function isWarehouseServiceSupported(service: {
  accountingTreatment: WarehouseAccountingTreatment;
  billingBasis: WarehouseBillingBasis;
}): { supported: boolean; reason?: string } {
  if (
    service.accountingTreatment === 'capitalizable' &&
    ['fixed', 'weight', 'volume', 'time'].includes(service.billingBasis)
  ) {
    return {
      supported: false,
      reason:
        'This capitalizable service needs a physical allocation measure that is not available here.',
    };
  }
  return { supported: true };
}

type ChargeLike = Pick<WarehouseWorkChargeResponse, 'charge'>;
export type WarehouseChargeGroups<T extends ChargeLike = ChargeLike> = {
  unbilled: T[];
  draft: T[];
  sent: T[];
  completed: T[];
};

export function groupWarehouseCharges<T extends ChargeLike>(
  charges: T[]
): WarehouseChargeGroups<T> {
  const groups: WarehouseChargeGroups<T> = { unbilled: [], draft: [], sent: [], completed: [] };
  for (const charge of charges) {
    const status = charge.charge.activeInvoice?.status;
    if (!status) groups.unbilled.push(charge);
    else if (status === 'draft') groups.draft.push(charge);
    else if (status === 'submitted' || status === 'disputed') groups.sent.push(charge);
    else groups.completed.push(charge);
  }
  return groups;
}

export function eligibleWarehouseCharges<T extends ChargeLike>(
  charges: T[],
  warehouseId: string,
  currency: string
): T[] {
  const normalizedCurrency = currency.trim().toUpperCase();
  return charges.filter(
    ({ charge }) =>
      charge.warehouseId === warehouseId &&
      charge.currency === normalizedCurrency &&
      charge.amountCents > 0 &&
      charge.allocationStatus === 'ready' &&
      charge.activeInvoice === null
  );
}

export function createIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `warehouse-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function stableSerialize(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableSerialize(entry)}`)
    .join(',')}}`;
}

export type WarehouseIdempotencyController = {
  keyFor(value: unknown): string;
};

export function createWarehouseIdempotencyController(): WarehouseIdempotencyController {
  let current: { serialized: string; key: string } | null = null;
  return {
    keyFor(value: unknown) {
      const serialized = stableSerialize(value);
      if (!current || current.serialized !== serialized) {
        current = { serialized, key: createIdempotencyKey() };
      }
      return current.key;
    },
  };
}

/** React form helper: retries retain the key; changing the semantic payload rotates it. */
export function useWarehouseIdempotencyKey(value: unknown): string {
  const controllerRef = useRef<WarehouseIdempotencyController | null>(null);
  if (!controllerRef.current) controllerRef.current = createWarehouseIdempotencyController();
  return controllerRef.current.keyFor(value);
}

/** Build one receipt request envelope before mutateAsync; retries reuse it verbatim. */
export function createReceiptRequestEnvelope(
  input: Omit<CreateReceiptRequest, 'idempotencyKey'>,
  uuidSource: () => string = createIdempotencyKey
): CreateReceiptRequest {
  return { ...input, idempotencyKey: uuidSource() };
}

type ReceiptRequestInput = Omit<CreateReceiptRequest, 'idempotencyKey'>;

export type ReceiptRequestController = {
  prepare(input: ReceiptRequestInput): CreateReceiptRequest;
  acknowledge(request: Pick<CreateReceiptRequest, 'idempotencyKey'>): void;
};

/** Keep one exact receipt envelope pending until its server response is acknowledged. */
export function createReceiptRequestController(
  uuidSource: () => string = createIdempotencyKey
): ReceiptRequestController {
  let pending: { semanticInput: string; request: CreateReceiptRequest } | null = null;
  return {
    prepare(input) {
      const semanticInput = stableSerialize(input);
      if (pending?.semanticInput === semanticInput) return pending.request;
      const request = createReceiptRequestEnvelope(input, uuidSource);
      pending = { semanticInput, request };
      return request;
    },
    acknowledge(request) {
      if (pending?.request.idempotencyKey === request.idempotencyKey) pending = null;
    },
  };
}

function isStaleInvoiceError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = 'status' in error && typeof error.status === 'number' ? error.status : null;
  const details = 'details' in error ? error.details : null;
  const code =
    details && typeof details === 'object' && details !== null && 'error' in details
      ? (details.error as { code?: unknown })?.code
      : null;
  return status === 409 && (code === 'STALE_INVOICE' || /stale|version/i.test(error.message));
}

type WarehouseQueryEnabled = boolean;

function queryString(params: Record<string, string | string[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    for (const item of Array.isArray(value) ? value : [value]) search.append(key, item);
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export function useWarehouseFacilities(enabled: WarehouseQueryEnabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.facilities(),
    enabled,
    queryFn: () =>
      apiFetch('/api/warehouse/billing/facilities', { responseSchema: z.array(WarehouseFacility) }),
  });
}

/** R1 item 4 — every attestation the tenant currently has, with staleness
 * computed server-side on read. Owner-gated by the caller (FinancePage),
 * not by this hook — the API itself allows any warehouse-module role to
 * read it (matches invoice-read semantics). */
export function useWarehousePeriodExpenseAttestations(enabled: WarehouseQueryEnabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.periodExpenseAttestations(),
    enabled,
    queryFn: () =>
      apiFetch('/api/warehouse/billing/period-expenses/attestations', {
        responseSchema: ListWarehousePeriodExpenseAttestationsResponse,
      }),
  });
}

/** Declares every warehouse expense for a month is entered — recomputed
 * and re-verified server-side; this mutation carries no count/total, only
 * the month being attested. Refetches both the attestation list and
 * finance-summary (whose income gate the attestation unlocks). */
export function useAttestWarehousePeriodExpenses() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (body: AttestWarehousePeriodExpensesRequest) =>
      apiFetch('/api/warehouse/billing/period-expenses/attest', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: AttestWarehousePeriodExpensesResponse,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: WAREHOUSE_BILLING_QUERY_KEYS.periodExpenseAttestations(),
      });
      void qc.invalidateQueries({ queryKey: ['finance-summary'] });
    },
  });
}

/** R1 item 4 follow-up (2026-09-02, Faraz: "can I see a summary with a
 * test button instead of going into details?") — itemized per-month
 * preview of exactly what attesting `months` would cover. Feeds both the
 * Finance row summaries and the confirm dialog's itemization, plus the P&L
 * metric cards' "entered, not yet attested" detail. Read-only for any
 * tenant member — no owner gate here (the caller decides whether to show
 * the attest control). */
export function useWarehouseExpensePeriodPreview(
  months: readonly string[],
  enabled: WarehouseQueryEnabled = true
) {
  return useQuery({
    queryKey: ['expense-period-preview', months] as const,
    enabled: enabled && months.length > 0,
    queryFn: () =>
      apiFetch(
        `/api/warehouse/billing/period-expenses/preview?months=${encodeURIComponent(months.join(','))}`,
        { responseSchema: WarehouseExpensePeriodPreviewResponse }
      ),
  });
}

function isPrepConflict(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = 'status' in error && typeof error.status === 'number' ? error.status : null;
  const details = 'details' in error ? error.details : null;
  const code =
    details && typeof details === 'object' && details !== null && 'error' in details
      ? (details.error as { code?: unknown })?.code
      : null;
  return status === 409 || code === 'PREP_CONFLICT' || code === 'PREP_BOX_CONFLICT';
}

function invalidateWarehousePrepQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: WAREHOUSE_PREP_QUERY_KEYS.all });
}

function invalidateWarehousePrepHandoffQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  batchId: string
) {
  void queryClient.invalidateQueries({ queryKey: WAREHOUSE_PREP_QUERY_KEYS.handoff(batchId) });
  void queryClient.invalidateQueries({ queryKey: WAREHOUSE_PREP_QUERY_KEYS.batch(batchId) });
  void queryClient.invalidateQueries({ queryKey: WAREHOUSE_PREP_QUERY_KEYS.queue });
}

export function useWarehousePrepBatches(warehouseId?: string | null, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_PREP_QUERY_KEYS.queueFor(warehouseId),
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/warehouse/prep-batches${queryString({ warehouseId: warehouseId ?? undefined })}`,
        {
          responseSchema: ListWarehousePrepBatchesResponse,
        }
      ),
  });
}

export function useWarehousePrepBatch(batchId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_PREP_QUERY_KEYS.batch(batchId ?? ''),
    enabled: enabled && Boolean(batchId),
    queryFn: () =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}`, {
        responseSchema: WarehousePrepBatchResponse,
      }),
  });
}

export function useWarehousePrepBoxes(batchId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_PREP_QUERY_KEYS.boxes(batchId ?? ''),
    enabled: enabled && Boolean(batchId),
    queryFn: () =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/boxes`, {
        responseSchema: ListWarehousePrepBoxesResponse,
      }),
  });
}

/** Read-only Amazon handoff status. This never calls Amazon directly from the browser. */
export function useWarehousePrepAmazonHandoff(batchId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_PREP_QUERY_KEYS.handoff(batchId ?? ''),
    enabled: enabled && Boolean(batchId),
    queryFn: () =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/amazon-handoff`, {
        responseSchema: PrepAmazonHandoffResponse,
      }),
  });
}

export function useSyncWarehousePrepAmazonHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId }: { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/amazon-handoff/sync`, {
        method: 'POST',
        responseSchema: PrepAmazonHandoffResponse,
      }),
    onSettled: (_result, _error, { batchId }) =>
      invalidateWarehousePrepHandoffQueries(queryClient, batchId),
  });
}

export function useConfirmWarehousePrepAmazonHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId, ...body }: ConfirmPrepAmazonHandoffRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/amazon-handoff/confirm`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: PrepAmazonHandoffResponse,
      }),
    onSettled: (_result, _error, { batchId }) =>
      invalidateWarehousePrepHandoffQueries(queryClient, batchId),
  });
}

export function useRetryWarehousePrepAmazonLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId, ...body }: RetryPrepAmazonLabelsRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/labels/retry`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: PrepAmazonHandoffResponse,
      }),
    onSettled: (_result, _error, { batchId }) =>
      invalidateWarehousePrepHandoffQueries(queryClient, batchId),
  });
}

export function useDispatchWarehousePrepBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId, ...body }: DispatchWarehousePrepBatchRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/dispatch`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepDispatchResponse,
      }),
    onSettled: (_result, _error, { batchId }) =>
      invalidateWarehousePrepHandoffQueries(queryClient, batchId),
  });
}

const DISPATCH_RECEIPT_STORAGE_PREFIX = 'sourcito:warehouse-prep-dispatch:';
const StoredWarehousePrepDispatch = z
  .object({
    replayed: z.boolean(),
    dispatch: WarehousePrepDispatchResponse.shape.dispatch
      .omit({ dispatchedBy: true })
      .extend({
        dispatchedBy: z.object({ displayName: z.string().trim().min(1) }).strict(),
      })
      .strict(),
  })
  .strict();
export type WarehousePrepDispatchPresentation = z.infer<typeof StoredWarehousePrepDispatch>;

export function rememberWarehousePrepDispatch(
  tenantId: string | undefined,
  prepReference: string,
  response: WarehousePrepDispatchResponse
) {
  if (typeof window === 'undefined' || !tenantId) return;
  try {
    const stored: WarehousePrepDispatchPresentation = {
      replayed: response.replayed,
      dispatch: {
        ...response.dispatch,
        dispatchedBy: { displayName: response.dispatch.dispatchedBy.displayName },
      },
    };
    window.sessionStorage.setItem(
      `${DISPATCH_RECEIPT_STORAGE_PREFIX}${tenantId}:${prepReference}`,
      JSON.stringify(stored)
    );
  } catch {
    // Browser storage can be unavailable or full; the server remains authoritative.
  }
}

export function readWarehousePrepDispatch(
  tenantId: string | undefined,
  prepReference: string
): WarehousePrepDispatchPresentation | undefined {
  if (typeof window === 'undefined' || !tenantId) return undefined;
  try {
    const stored = window.sessionStorage.getItem(
      `${DISPATCH_RECEIPT_STORAGE_PREFIX}${tenantId}:${prepReference}`
    );
    if (!stored) return undefined;
    const parsed = StoredWarehousePrepDispatch.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function dispatchRequestStorageKey(tenantId: string, prepReference: string) {
  return `${DISPATCH_RECEIPT_STORAGE_PREFIX}${tenantId}:${prepReference}:request`;
}

export function rememberWarehousePrepDispatchRequest(
  tenantId: string | undefined,
  prepReference: string,
  request: DispatchWarehousePrepBatchRequest
) {
  if (typeof window === 'undefined' || !tenantId) return;
  try {
    window.sessionStorage.setItem(
      dispatchRequestStorageKey(tenantId, prepReference),
      JSON.stringify(DispatchWarehousePrepBatchRequest.parse(request))
    );
  } catch {
    // Browser storage can be unavailable or full; the server remains authoritative.
  }
}

export function readWarehousePrepDispatchRequest(
  tenantId: string | undefined,
  prepReference: string
): DispatchWarehousePrepBatchRequest | undefined {
  if (typeof window === 'undefined' || !tenantId) return undefined;
  try {
    const stored = window.sessionStorage.getItem(
      dispatchRequestStorageKey(tenantId, prepReference)
    );
    if (!stored) return undefined;
    const parsed = DispatchWarehousePrepBatchRequest.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

export function forgetWarehousePrepDispatchRequest(
  tenantId: string | undefined,
  prepReference: string
) {
  if (typeof window === 'undefined' || !tenantId) return;
  try {
    window.sessionStorage.removeItem(dispatchRequestStorageKey(tenantId, prepReference));
  } catch {
    // Browser storage can be unavailable; no server state is affected.
  }
}

/** Human prep-box references are the only label identifiers rendered by the workstation. */
export function warehousePrepAmazonLabelUrl(batchId: string, boxReference: string) {
  return `/api/warehouse/prep-batches/${encodeURIComponent(batchId)}/labels/${encodeURIComponent(boxReference)}`;
}

/** Download only the tenant-authenticated, reference-bound label endpoint. */
export async function downloadWarehousePrepAmazonLabel(batchId: string, boxReference: string) {
  let response: Response;
  try {
    response = await fetch(warehousePrepAmazonLabelUrl(batchId, boxReference), {
      credentials: 'include',
    });
  } catch {
    throw new Error('The label could not be fetched. Check your connection and try again.');
  }
  if (!response.ok) throw new Error(`The label is unavailable (${response.status}). Try again.`);
  if (!response.headers.get('content-type')?.toLowerCase().includes('pdf'))
    throw new Error('The server did not return a PDF label. Try again.');
  let blob: Blob;
  try {
    blob = await response.blob();
  } catch {
    throw new Error('The label file could not be read. Try again.');
  }
  if (!blob.size) throw new Error('The label file was empty. Try again.');
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `${boxReference}-label.pdf`;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } catch {
    throw new Error('Your browser blocked the label download. Allow downloads and try again.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function useClaimWarehousePrepBatch() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId, ...body }: ClaimWarehousePrepBatchRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/claim`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepBatchResponse,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useMapWarehousePrepLine() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      lineId,
      ...body
    }: MapWarehousePrepLineRequest & { batchId: string; lineId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/lines/${lineId}/mapping`, {
        method: 'PUT',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepBatchResponse,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useCreateWarehousePrepBox() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({ batchId }: { batchId: string }) =>
      apiFetch<WarehousePrepBox>(`/api/warehouse/prep-batches/${batchId}/boxes`, {
        method: 'POST',
        body: JSON.stringify({}),
        responseSchema: WarehousePrepBox,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useReplaceWarehousePrepBoxContents() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      boxId,
      ...body
    }: ReplaceWarehousePrepBoxContentsRequest & { batchId: string; boxId: string }) =>
      apiFetch<WarehousePrepBox>(`/api/warehouse/prep-batches/${batchId}/boxes/${boxId}/contents`, {
        method: 'PUT',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepBox,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useFinalizeWarehousePrepBox() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      boxId,
      ...body
    }: FinalizeWarehousePrepBoxRequest & { batchId: string; boxId: string }) =>
      apiFetch<WarehousePrepBox>(`/api/warehouse/prep-batches/${batchId}/boxes/${boxId}/finalize`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepBox,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useReopenWarehousePrepBox() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      boxId,
      ...body
    }: ReopenWarehousePrepBoxRequest & { batchId: string; boxId: string }) =>
      apiFetch<WarehousePrepBox>(`/api/warehouse/prep-batches/${batchId}/boxes/${boxId}/reopen`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehousePrepBox,
      }),
    onSettled: () => invalidateWarehousePrepQueries(qc),
  });
}

export function useCreateWarehousePrepWorkCharge() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      ...body
    }: CreateWarehousePrepWorkChargeRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/work-charges`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehouseWorkChargeResponse,
      }),
    onSettled: () => {
      invalidateWarehousePrepQueries(qc);
      void qc.invalidateQueries({ queryKey: ['warehouse-billing', 'charges'] });
    },
    onError: (error) => {
      if (isPrepConflict(error)) invalidateWarehousePrepQueries(qc);
    },
  });
}

export function useWarehouseServices(warehouseId?: string | null, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.services(warehouseId),
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/warehouse/billing/services${queryString({ warehouseId: warehouseId ?? undefined })}`,
        {
          responseSchema: z.array(WarehouseServiceCatalogItem),
        }
      ),
  });
}

export function useWarehouseCartons(warehouseId?: string | null, enabled = true) {
  return useQuery({
    queryKey: ['warehouse-prep', 'cartons', warehouseId ?? 'all'] as const,
    enabled: enabled && Boolean(warehouseId),
    queryFn: () =>
      apiFetch(`/api/warehouse/cartons${queryString({ warehouseId: warehouseId ?? undefined })}`, {
        responseSchema: ListWarehouseCartonsResponse,
      }),
  });
}

export function useWarehousePrepCartonRecommendations() {
  return useMutation({
    retry: false,
    mutationFn: ({
      batchId,
      ...body
    }: WarehouseCartonRecommendationRequest & { batchId: string }) =>
      apiFetch(`/api/warehouse/prep-batches/${batchId}/carton-recommendations`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehouseCartonRecommendationResponse,
      }),
  });
}

export function useWarehouseBillingShipments(warehouseId?: string | null, enabled = true) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.shipments(warehouseId),
    enabled,
    queryFn: async () => {
      const shipments = await apiFetch('/api/warehouse/shipments', {
        responseSchema: ListWarehouseShipmentsResponse,
      });
      return warehouseId == null
        ? shipments
        : shipments.filter((shipment) => shipment.warehouseId === warehouseId);
    },
  });
}

export function useWarehouseShipmentBoxes(
  warehouseId: string | null | undefined,
  shipmentId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.boxes(warehouseId, shipmentId ?? ''),
    enabled: enabled && Boolean(shipmentId),
    queryFn: () =>
      apiFetch(`/api/warehouse/billing/shipments/${shipmentId}/boxes`, {
        responseSchema: ListWarehouseShipmentBoxesResponse,
      }),
  });
}

export type WarehouseWorkChargesFilters = {
  warehouseId?: string | null;
  shipmentId?: string | null;
  sourceType?: 'receipt' | 'shipment' | 'shipment_line' | 'box';
  allocationStatus?: 'ready' | 'blocked';
};

export function useWarehouseWorkCharges(filters: WarehouseWorkChargesFilters = {}, enabled = true) {
  const { warehouseId, shipmentId, sourceType, allocationStatus } = filters;
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.charges(
      warehouseId,
      shipmentId,
      JSON.stringify({ sourceType, allocationStatus })
    ),
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/warehouse/billing/work-charges${queryString({ warehouseId: warehouseId ?? undefined, shipmentId: shipmentId ?? undefined, sourceType, allocationStatus })}`,
        {
          responseSchema: ListWarehouseWorkChargesResponse,
        }
      ),
  });
}

export type WarehouseInvoiceFilters = {
  warehouseId?: string | null;
  status?: WarehouseInvoiceStatus | WarehouseInvoiceStatus[];
  currency?: string;
};

function normalizeWarehouseInvoiceFilters(filters: WarehouseInvoiceFilters): Omit<
  WarehouseInvoiceFilters,
  'status' | 'currency'
> & {
  status?: WarehouseInvoiceStatus[];
  currency?: string;
} {
  const status =
    filters.status === undefined
      ? undefined
      : [...new Set(Array.isArray(filters.status) ? filters.status : [filters.status])].sort();
  const currency = filters.currency?.trim().toUpperCase();
  return { warehouseId: filters.warehouseId ?? undefined, status, currency };
}

export function useWarehouseInvoices(filters: WarehouseInvoiceFilters = {}, enabled = true) {
  const { warehouseId, status, currency } = normalizeWarehouseInvoiceFilters(filters);
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.invoices(
      warehouseId,
      JSON.stringify({ status, currency })
    ),
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/warehouse/billing/invoices${queryString({ warehouseId: warehouseId ?? undefined, status, currency })}`,
        {
          responseSchema: ListWarehouseInvoicesResponse,
        }
      ),
  });
}

export function useWarehouseInvoice(
  warehouseId: string | null | undefined,
  invoiceId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.invoice(warehouseId, invoiceId ?? ''),
    enabled: enabled && Boolean(invoiceId),
    queryFn: () =>
      apiFetch(`/api/warehouse/billing/invoices/${invoiceId}`, {
        responseSchema: WarehouseInvoiceDetailResponse,
      }),
  });
}

export function invalidateWarehouseBillingQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  options: {
    warehouseId?: string | null;
    shipmentId?: string | null;
    invoiceId?: string | null;
  } = {}
) {
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'facilities'] });
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'services'] });
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'shipments'] });
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'boxes'] });
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'charges'] });
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'invoices'] });
  if (options.invoiceId) {
    if (options.warehouseId) {
      void queryClient.invalidateQueries({
        queryKey: WAREHOUSE_BILLING_QUERY_KEYS.invoice(options.warehouseId, options.invoiceId),
      });
    } else {
      void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'invoice'] });
    }
  }
}

export function useAssignShipmentFacility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, warehouseId }: { shipmentId: string; warehouseId: string }) =>
      apiFetch<{ shipmentId: string; warehouseId: string }>(
        `/api/warehouse/billing/shipments/${shipmentId}/facility`,
        {
          method: 'PATCH',
          body: JSON.stringify({ warehouseId }),
          responseSchema: z.object({ shipmentId: Uuid, warehouseId: Uuid }),
        }
      ),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
  });
}
export const useAssignWarehouseShipmentFacility = useAssignShipmentFacility;

export function useCreateShipmentBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      ...body
    }: CreateWarehouseShipmentBoxRequest & { shipmentId: string }) =>
      apiFetch<WarehouseShipmentBox>(`/api/warehouse/billing/shipments/${shipmentId}/boxes`, {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehouseShipmentBox,
      }),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
  });
}

export function useReplaceShipmentBoxContents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      shipmentId,
      boxId,
      ...body
    }: ReplaceWarehouseShipmentBoxContentsRequest & { shipmentId: string; boxId: string }) =>
      apiFetch<WarehouseShipmentBox>(
        `/api/warehouse/billing/shipments/${shipmentId}/boxes/${boxId}/contents`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
          responseSchema: WarehouseShipmentBox,
        }
      ),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
  });
}

export function useFinalizeShipmentBox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, boxId }: { shipmentId: string; boxId: string }) =>
      apiFetch<WarehouseShipmentBox>(
        `/api/warehouse/billing/shipments/${shipmentId}/boxes/${boxId}/finalize`,
        {
          method: 'POST',
          responseSchema: WarehouseShipmentBox,
        }
      ),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
  });
}

export function useCreateWarehouseWorkCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWarehouseWorkChargeRequest) =>
      apiFetch<WarehouseWorkChargeResponse>('/api/warehouse/billing/work-charges', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehouseWorkChargeResponse,
      }),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['warehouse-billing', 'charges'] }),
  });
}
export const useCreateWorkCharge = useCreateWarehouseWorkCharge;

export function useReverseWarehouseWorkCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chargeId, ...body }: ReverseWarehouseWorkChargeRequest & { chargeId: string }) =>
      apiFetch<WarehouseWorkChargeResponse>(
        `/api/warehouse/billing/work-charges/${chargeId}/reverse`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          responseSchema: WarehouseWorkChargeResponse,
        }
      ),
    onSuccess: () => invalidateWarehouseBillingQueries(qc),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['warehouse-billing', 'charges'] });
      invalidateWarehousePrepQueries(qc);
    },
  });
}

export function useCreateWarehouseInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWarehouseInvoiceRequest) =>
      apiFetch('/api/warehouse/billing/invoices', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: WarehouseInvoiceDetailResponse,
      }),
    onSuccess: (detail) =>
      invalidateWarehouseBillingQueries(qc, {
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
      }),
  });
}

export function useUpdateWarehouseInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      ...body
    }: UpdateWarehouseInvoiceDraftRequest & { invoiceId: string }) =>
      apiFetch(`/api/warehouse/billing/invoices/${invoiceId}/draft`, {
        method: 'PUT',
        body: JSON.stringify(body),
        responseSchema: WarehouseInvoiceDetailResponse,
      }),
    onSuccess: (detail) =>
      invalidateWarehouseBillingQueries(qc, {
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
      }),
    onError: (error: unknown) => {
      if (isStaleInvoiceError(error)) {
        void qc.invalidateQueries({ queryKey: ['warehouse-billing', 'invoice'] });
      }
    },
  });
}

export function useSubmitWarehouseInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      expectedVersion,
      idempotencyKey,
    }: {
      invoiceId: string;
      expectedVersion: number;
      idempotencyKey: string;
    }) =>
      apiFetch(`/api/warehouse/billing/invoices/${invoiceId}/transition`, {
        method: 'POST',
        body: JSON.stringify({ action: 'submit', expectedVersion, idempotencyKey }),
        responseSchema: WarehouseInvoiceDetailResponse,
      }),
    onSuccess: (detail) =>
      invalidateWarehouseBillingQueries(qc, {
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
      }),
    onError: (error: unknown) => {
      if (isStaleInvoiceError(error)) {
        void qc.invalidateQueries({ queryKey: ['warehouse-billing', 'invoice'] });
      }
    },
  });
}

type WarehouseInvoiceMutationContext = {
  invoiceId: string;
  warehouseId: string;
};

function invalidateWarehouseInvoiceQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  invoices: WarehouseInvoiceMutationContext[]
) {
  void queryClient.invalidateQueries({ queryKey: ['warehouse-billing', 'invoices'] });
  const seen = new Set<string>();
  for (const { invoiceId, warehouseId } of invoices) {
    for (const queryKey of [
      WAREHOUSE_BILLING_QUERY_KEYS.invoice(warehouseId, invoiceId),
      WAREHOUSE_BILLING_QUERY_KEYS.invoice(undefined, invoiceId),
    ] as const) {
      const key = JSON.stringify(queryKey);
      if (seen.has(key)) continue;
      seen.add(key);
      void queryClient.invalidateQueries({ queryKey });
    }
  }
}

export function useTransitionWarehouseInvoice() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      invoiceId,
      warehouseId: _warehouseId,
      ...body
    }: TransitionWarehouseInvoiceRequest & WarehouseInvoiceMutationContext) =>
      apiFetch<WarehouseInvoiceDetailResponse>(
        `/api/warehouse/billing/invoices/${invoiceId}/transition`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          responseSchema: WarehouseInvoiceDetailResponse,
        }
      ),
    onSettled: (detail, _error, variables) => {
      invalidateWarehouseInvoiceQueries(qc, [
        variables,
        ...(detail
          ? [{ invoiceId: detail.invoice.id, warehouseId: detail.invoice.warehouseId }]
          : []),
      ]);
      if (variables.action === 'approve') {
        void qc.invalidateQueries({ queryKey: ['finance-summary'] });
      }
    },
  });
}

export function useCreateWarehouseCreditNote() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      invoiceId,
      warehouseId: _warehouseId,
      ...body
    }: CreateWarehouseCreditNoteRequest & WarehouseInvoiceMutationContext) =>
      apiFetch<WarehouseInvoiceDetailResponse>(
        `/api/warehouse/billing/invoices/${invoiceId}/credit-notes`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          responseSchema: WarehouseInvoiceDetailResponse,
        }
      ),
    onSettled: (detail, _error, variables) => {
      invalidateWarehouseInvoiceQueries(qc, [
        { invoiceId: variables.invoiceId, warehouseId: variables.warehouseId },
        ...(detail
          ? [{ invoiceId: detail.invoice.id, warehouseId: detail.invoice.warehouseId }]
          : []),
      ]);
    },
  });
}

export function useRecordWarehouseInvoicePayment() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      invoiceId,
      warehouseId: _warehouseId,
      ...body
    }: RecordWarehouseInvoicePaymentRequest & WarehouseInvoiceMutationContext) =>
      apiFetch<RecordWarehouseInvoicePaymentResponse>(
        `/api/warehouse/billing/invoices/${invoiceId}/payments`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          responseSchema: RecordWarehouseInvoicePaymentResponse,
        }
      ),
    onSettled: (response, _error, variables) => {
      invalidateWarehouseInvoiceQueries(qc, [
        variables,
        {
          ...(response
            ? {
                invoiceId: response.invoice.invoice.id,
                warehouseId: response.invoice.invoice.warehouseId,
              }
            : { invoiceId: variables.invoiceId, warehouseId: variables.warehouseId }),
        },
      ]);
    },
  });
}

export function useReverseWarehouseInvoicePayment() {
  const qc = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      invoiceId,
      paymentId,
      warehouseId: _warehouseId,
      ...body
    }: ReverseWarehouseInvoicePaymentRequest &
      WarehouseInvoiceMutationContext & { paymentId: string }) =>
      apiFetch<ReverseWarehouseInvoicePaymentResponse>(
        `/api/warehouse/billing/invoices/${invoiceId}/payments/${paymentId}/reverse`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          responseSchema: ReverseWarehouseInvoicePaymentResponse,
        }
      ),
    onSettled: (response, _error, variables) => {
      invalidateWarehouseInvoiceQueries(qc, [
        variables,
        {
          ...(response
            ? {
                invoiceId: response.invoice.invoice.id,
                warehouseId: response.invoice.invoice.warehouseId,
              }
            : { invoiceId: variables.invoiceId, warehouseId: variables.warehouseId }),
        },
      ]);
    },
  });
}

/**
 * M2 Warehouse receiving. Money-free — 3PL staff count units, costs stay
 * tenant-side. Both hooks assume the caller is behind useModuleEnabled('warehouse').
 */

export function useReceivablePos(enabled = true) {
  return useQuery({
    queryKey: ['receivable-pos'] as const,
    enabled,
    queryFn: () =>
      apiFetch('/api/warehouse/receivable', { responseSchema: ListReceivablePosResponse }),
  });
}

export function useCreateReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReceiptRequest) =>
      apiFetch('/api/warehouse/receipts', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateReceiptResponse.strict(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['receivable-pos'] });
      void queryClient.invalidateQueries({ queryKey: ['receipt-history'] });
      void queryClient.invalidateQueries({ queryKey: WAREHOUSE_PREP_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

/** R2 item 9 (2026-09-03 intake sanity checks) fix pass I7: a human
 *  affirms an inferred (invoice-derived, never physically counted) receipt
 *  is accurate. Owner/operator only — the route 403s anyone else. */
export function useConfirmReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (receiptId: string) =>
      apiFetch(`/api/warehouse/receipts/${receiptId}/confirm`, {
        method: 'POST',
        responseSchema: ConfirmReceiptResponse,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['receipt-history'] });
    },
  });
}

/** Open shipments + label files for the floor (A4). */
export function useWarehouseShipments(
  warehouseIdOrEnabled: string | null | boolean = null,
  enabled = true
) {
  const legacyEnabled = typeof warehouseIdOrEnabled === 'boolean';
  const warehouseId = legacyEnabled ? undefined : warehouseIdOrEnabled;
  const queryEnabled = legacyEnabled ? warehouseIdOrEnabled : enabled;
  return useWarehouseBillingShipments(warehouseId, queryEnabled);
}

// ─────────────────────────────────────────────────────────
// Receipt HISTORY (Faraz: "if there is no current receiving, there should
// be a way to find the previous/old one, and searchable"). Search is a pure
// helper (unit-tested in warehouse.test.ts) mirroring lib/orders.ts's
// matchesOrderSearch — case-insensitive substring match across the PO ref
// and every line's identity fields, so a floor worker can find an old
// receipt by item#, UPC, or product name as easily as by PO number.
// ─────────────────────────────────────────────────────────

export function useReceiptHistory(enabled = true) {
  return useQuery({
    queryKey: ['receipt-history'] as const,
    enabled,
    queryFn: () =>
      apiFetch('/api/warehouse/receipts', { responseSchema: ListReceiptHistoryResponse }),
  });
}

export type ReceiptSearchLine = Pick<
  ReceiptHistoryLine,
  'itemNo' | 'upc' | 'description' | 'productName'
>;
export type ReceiptSearchPoRef = Pick<ReceiptHistoryPoRef, 'vendor' | 'externalId'>;

export function matchesReceiptSearch(
  receipt: { po: ReceiptSearchPoRef; lines: ReceiptSearchLine[] },
  query: string
): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  if (receipt.po.vendor.toLowerCase().includes(trimmed)) return true;
  if (receipt.po.externalId?.toLowerCase().includes(trimmed)) return true;
  return receipt.lines.some(
    (l) =>
      (l.itemNo?.toLowerCase().includes(trimmed) ?? false) ||
      (l.upc?.toLowerCase().includes(trimmed) ?? false) ||
      l.description.toLowerCase().includes(trimmed) ||
      (l.productName?.toLowerCase().includes(trimmed) ?? false)
  );
}

export function searchReceiptHistory<
  T extends { po: ReceiptSearchPoRef; lines: ReceiptSearchLine[] },
>(receipts: T[], query: string): T[] {
  return receipts.filter((r) => matchesReceiptSearch(r, query));
}

// ─────────────────────────────────────────────────────────
// Receipt HISTORY row redesign (Faraz, 2026-08-25: "another example of an
// unattractive UI. We can do better.") ReceiptHistoryItem already carries
// every line inline (with productName/imageUrl/asin via the same
// imagesByNormalizedItemNo join the receivable-PO form uses) — there's no
// separate summary/detail split to bridge the way FbaShipmentSummary needed
// linePreview, so these are pure client-side derivations over `lines`, not
// an API addition.
// ─────────────────────────────────────────────────────────

/** "438 units across 4 lines" (+", N damaged" when any line has damage).
 *  Singular/plural-correct at the boundary of 1. */
export function receiptQtyStory(receipt: {
  lines: Pick<ReceiptHistoryLine, 'qtyCounted' | 'qtyDamaged'>[];
}): string {
  const unit = (n: number) => `${n} unit${n === 1 ? '' : 's'}`;
  const line = (n: number) => `${n} line${n === 1 ? '' : 's'}`;
  const totalCounted = receipt.lines.reduce((s, l) => s + l.qtyCounted, 0);
  const totalDamaged = receipt.lines.reduce((s, l) => s + l.qtyDamaged, 0);
  const base = `${unit(totalCounted)} across ${line(receipt.lines.length)}`;
  return totalDamaged > 0 ? `${base}, ${totalDamaged} damaged` : base;
}

/** Mono per-line identifier for the preview row — item# preferred (matches
 *  the vendor invoice), UPC as fallback, null when neither is on file. */
export function receiptLineIdentifier(
  line: Pick<ReceiptHistoryLine, 'itemNo' | 'upc'>
): string | null {
  return line.itemNo ?? line.upc ?? null;
}

/** Top 3 receipt lines by qtyCounted desc (identifier asc tie-break) —
 *  mirrors the shipments linePreview CTE's qty-desc/id-asc rule, just
 *  computed client-side since every line is already in hand. */
export function receiptLinePreview<
  T extends Pick<ReceiptHistoryLine, 'id' | 'itemNo' | 'upc' | 'qtyCounted'>,
>(lines: T[], limit = 3): T[] {
  return [...lines]
    .sort((a, b) => {
      if (b.qtyCounted !== a.qtyCounted) return b.qtyCounted - a.qtyCounted;
      return (a.itemNo ?? a.upc ?? '').localeCompare(b.itemNo ?? b.upc ?? '');
    })
    .slice(0, limit);
}

// ─────────────────────────────────────────────────────────
// ReceivingPage filter pills (Faraz, 2026-08-25: "what if I want to see
// only open or pending or any shipment with issues" — applied to receiving
// too). Mirrors order-planning.ts's Reorder-table filter-pill convention.
// Composition order is search → filter, matching PlanningPage's
// ReorderSection pipeline (this list has no column sort).
//
// ASSUMPTION: ReceiptHistoryLine has no `qtyExpected` — that's on the PO
// line, not the receipt — so "with issues" can't literally test
// counted < expected from this payload. It uses `deliveryComplete === false`
// (the exact field the receiving form sets when "missing units will be
// claimed from the vendor" is left unchecked) OR'd with any line carrying
// recorded damage, as the closest data-backed proxy for a shortfall.
// ─────────────────────────────────────────────────────────

export type ReceiptFilterKey = 'all' | 'with_issues' | 'inferred' | 'manual';

export const DEFAULT_RECEIPT_FILTER: ReceiptFilterKey = 'all';

export const RECEIPT_FILTER_PILLS: { key: ReceiptFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'with_issues', label: 'With issues' },
  { key: 'inferred', label: 'Inferred' },
  { key: 'manual', label: 'Manual' },
];

/** Single source of truth for the Inferred/Manual provenance distinction —
 *  also used to drive the row's Inferred badge, so the badge and the pill
 *  can never disagree. */
export function isInferredReceipt(receipt: { notes: string | null }): boolean {
  return receipt.notes?.includes('INFERRED') ?? false;
}

export function receiptHasIssues(receipt: {
  deliveryComplete: boolean;
  lines: { qtyDamaged: number }[];
}): boolean {
  return receipt.deliveryComplete === false || receipt.lines.some((l) => l.qtyDamaged > 0);
}

type ReceiptFilterable = {
  notes: string | null;
  deliveryComplete: boolean;
  lines: { qtyDamaged: number }[];
};

export function matchesReceiptFilter(
  receipt: ReceiptFilterable,
  filter: ReceiptFilterKey
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'with_issues':
      return receiptHasIssues(receipt);
    case 'inferred':
      return isInferredReceipt(receipt);
    case 'manual':
      return !isInferredReceipt(receipt);
  }
}

export function filterReceiptHistory<T extends ReceiptFilterable>(
  receipts: T[],
  filter: ReceiptFilterKey
): T[] {
  return receipts.filter((r) => matchesReceiptFilter(r, filter));
}

/** Pure and total: every pill key present even for an empty list (all
 *  zero), so callers can render "Label · 0" without a fallback branch. */
export function receiptFilterCounts(
  receipts: ReceiptFilterable[]
): Record<ReceiptFilterKey, number> {
  const counts: Record<ReceiptFilterKey, number> = {
    all: receipts.length,
    with_issues: 0,
    inferred: 0,
    manual: 0,
  };
  for (const r of receipts) {
    if (receiptHasIssues(r)) counts.with_issues++;
    if (isInferredReceipt(r)) counts.inferred++;
    else counts.manual++;
  }
  return counts;
}
